import {
  $generateJSONFromSelectedNodes,
  ClipboardImportExtension,
  GetClipboardDataExtension,
  type ImportMimeTypeFunction,
} from "@lexical/clipboard";
import { $isCodeNode } from "@lexical/code-core";
import { $generateNodesFromDOM } from "@lexical/html";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createTabNode,
  $createTextNode,
  $getEditor,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  configExtension,
  COPY_COMMAND,
  CUT_COMMAND,
  defineExtension,
  PASTE_COMMAND,
  safeCast,
  type BaseSelection,
  type LexicalEditor,
  type LexicalExtension,
  type LexicalNode,
} from "lexical";
import {
  checkFragment,
  type FragmentNote,
  CLIPBOARD_MIME_TYPE,
  exceedsBytes,
  readClipboardPayload,
  writeClipboardPayload,
} from "../../core/clipboard";
import { DEFAULT_LIMITS, tableLimits, type DocumentLimits } from "../../core/limits";
import type { TokenRegistry } from "../../core/tokens";
import type { DocumentId, SerializedNodeLike } from "../../core/types";
import { DEFAULT_URL_POLICY, type UrlPolicy } from "../../core/url-policy";
import type { LimitName } from "../../core/validate";
import {
  $insertBlocks,
  $insertFragment,
  $serializeNodes,
  type BlockPlace,
  type FragmentSource,
} from "./fragment";
import { trusted } from "./trusted";

/** A paste that was refused whole. The document, its selection, and its history are as they were. */
export interface PasteRejection {
  reason: "limit-exceeded";
  limit: LimitName;
  /** The configured maximum that was exceeded. */
  max: number;
  /** Which form of the content was being read when the limit tripped. */
  source: FragmentSource;
}

/** A paste that went in, changed to fit. The changes never include text. */
export interface PasteAdjustment {
  notes: readonly FragmentNote[];
  source: FragmentSource;
}

export interface ClipboardConfig {
  /** Written into what is copied, and compared on paste: block IDs only survive within it. */
  documentId: DocumentId | null;
  limits: DocumentLimits;
  urlPolicy: UrlPolicy;
  tokens: TokenRegistry | null;
  /** Whether this editor has table nodes. Without them a pasted table comes in as lines of text. */
  tables: boolean;
  /** Whether an attachment may stand in this editor's document. Without that, its name comes in. */
  attachments: boolean;
  /** Told about every refused paste. */
  reject: { current: (rejection: PasteRejection) => void };
  /** Told about every paste that was changed to fit. */
  adjust: { current: (adjustment: PasteAdjustment) => void };
}

export interface ClipboardOutput {
  subscribe(listener: () => void): () => void;
  /** The last refused paste, until it is dismissed or another paste goes through. */
  getRejection(): PasteRejection | null;
  /** What the last paste had changed, until it is dismissed or another paste goes through. */
  getAdjustment(): PasteAdjustment | null;
  dismiss(): void;
}

type Report = { rejection: PasteRejection | null; adjustment: PasteAdjustment | null };
const NOTHING: Report = { rejection: null, adjustment: null };

interface Mounted {
  config: ClipboardConfig;
  report(next: Report): void;
}

/** Importers are configured once, for every editor; what they need per editor is found here. */
const mounted = new WeakMap<LexicalEditor, Mounted>();

const FALLBACK: Mounted = {
  config: {
    documentId: null,
    limits: DEFAULT_LIMITS,
    urlPolicy: DEFAULT_URL_POLICY,
    tokens: null,
    tables: false,
    attachments: false,
    reject: { current: () => {} },
    adjust: { current: () => {} },
  },
  report: () => {},
};

const $mounted = (): Mounted => mounted.get($getEditor()) ?? FALLBACK;

/** What `$insertFragment` and the intents need to know about this editor's paste rules. */
export function $fragmentOptions(): {
  documentId: DocumentId;
  limits: DocumentLimits;
  urlPolicy: UrlPolicy;
  tables: boolean;
  attachments: boolean;
  tokens?: TokenRegistry;
} {
  const { config } = $mounted();
  return {
    // A payload that names no document never matches, so an editor without one keeps no IDs.
    documentId: config.documentId ?? "",
    limits: config.limits,
    urlPolicy: config.urlPolicy,
    tables: config.tables,
    attachments: config.attachments,
    ...(config.tokens === null ? {} : { tokens: config.tokens }),
  };
}

/**
 * Puts blocks that did not come from a paste through the same gate as one, and then beside a
 * block instead of at the selection: see `$insertBlocks`. Answers with the first node it made, or
 * `null` when the gate or the engine would not have the content. What loads on demand is handed
 * this function rather than importing it, so that it shares no module with the code around it.
 */
export function $placeBlocks(
  nodes: readonly SerializedNodeLike[],
  place: BlockPlace | null,
): LexicalNode | null {
  const options = $fragmentOptions();
  const fragment = checkFragment([...nodes], { ...options, sourceDocumentId: null });
  if (fragment.status !== "ok") return null;
  return $insertBlocks(fragment, place, options.limits)?.[0] ?? null;
}

/** Consumes the paste: nothing is inserted, and no other format of the same content is tried. */
function $refuse(limit: LimitName, max: number, source: FragmentSource): true {
  const rejection: PasteRejection = { reason: "limit-exceeded", limit, max, source };
  const { config, report } = $mounted();
  report({ rejection, adjustment: null });
  config.reject.current(rejection);
  return true;
}

/** Content that passed the check and still cannot go where the selection is, for its size. */
function $refuseInsert(
  outcome: "too-deep" | "table-too-large",
  limits: DocumentLimits,
  source: FragmentSource,
): true {
  if (outcome === "too-deep") return $refuse("depth", limits.maxDepth, source);
  const table = tableLimits(limits);
  return $refuse("table", table.rows * table.columns, source);
}

/** The paste went in. If the gate changed it to fit, the user and the host are told what. */
const $accepted = (notes: readonly FragmentNote[] = [], source: FragmentSource = "text"): true => {
  const { config, report } = $mounted();
  if (notes.length === 0) {
    report(NOTHING);
    return true;
  }
  const adjustment: PasteAdjustment = { notes, source };
  report({ rejection: null, adjustment });
  config.adjust.current(adjustment);
  return true;
};

/**
 * Lexical's DOM import recurses once per level, so markup is measured first, iteratively. Browsers'
 * HTML parsers already stop nesting at a few hundred levels (512 in Chromium and WebKit), which is
 * what actually protects the stack; this is the same guarantee without relying on that. Real pages
 * nest a few dozen levels of wrappers, which import flattens.
 */
const MAX_HTML_DEPTH = 256;

function measureDom(root: Node, maxNodes: number): "depth" | "nodes" | null {
  // Wrappers and whitespace never become nodes, so the count is allowed some slack; the fragment
  // is counted exactly afterwards.
  const budget = maxNodes * 8;
  let count = 0;
  const stack: { node: Node; depth: number }[] = [{ node: root, depth: 0 }];
  while (stack.length > 0) {
    const { node, depth } = stack.pop()!;
    if (depth > MAX_HTML_DEPTH) return "depth";
    if (++count > budget) return "nodes";
    for (let child = node.firstChild; child !== null; child = child.nextSibling) {
      stack.push({ node: child, depth: depth + 1 });
    }
  }
  return null;
}

const $importInternal: ImportMimeTypeFunction = (data, selection) => {
  const options = $fragmentOptions();
  const result = readClipboardPayload(data, options);
  if (result.status === "too-large") return $refuse(result.limit, result.max, "internal");
  // Forged, damaged, or newer than this build: the HTML beside it is the next best thing.
  if (result.status === "declined") return false;
  const outcome = $insertFragment(result, selection, options.limits);
  if (outcome === "unparseable") return false;
  if (outcome === "inserted") return $accepted(result.notes, "internal");
  return $refuseInsert(outcome, options.limits, "internal");
};

const $importHtml: ImportMimeTypeFunction = (html, selection) => {
  const options = $fragmentOptions();
  const { limits } = options;
  if (exceedsBytes(html, limits.maxBytes)) return $refuse("bytes", limits.maxBytes, "html");
  let generated: LexicalNode[];
  let images = false;
  try {
    const dom = new DOMParser().parseFromString(trusted(html), "text/html");
    images = dom.body.querySelector("img") !== null;
    const over = measureDom(dom.body, limits.maxNodes);
    if (over === "nodes") return $refuse("nodes", limits.maxNodes, "html");
    // Too deep to walk is not too large to paste: its plain text is the next format.
    if (over === "depth") return false;
    generated = $generateNodesFromDOM($getEditor(), dom);
  } catch {
    return false;
  }
  // Whatever Lexical made of the markup is content like any other: it goes through the same check
  // as a stored document, and what is inserted is built from the checked form, not from the DOM.
  // Nodes made here carry no block IDs, so there is no source document to name.
  const result = checkFragment($serializeNodes(generated), { ...options, sourceDocumentId: null });
  if (result.status === "too-large") return $refuse(result.limit, result.max, "html");
  if (result.status === "declined") return false;
  const outcome = $insertFragment(result, selection, limits);
  if (outcome === "unparseable") return false;
  if (outcome === "inserted") {
    // Lexical makes nothing of an `<img>`, so the text goes in without it. Said, not silent.
    return $accepted(images ? [...result.notes, "images-dropped"] : result.notes, "html");
  }
  return $refuseInsert(outcome, limits, "html");
};

/** Lines become paragraphs and tabs stay tabs, as Lexical's own plain-text paste has it. */
export function $paragraphsFromText(text: string): LexicalNode[] {
  return text.split(/\r?\n/).map((line) => {
    const paragraph = $createParagraphNode();
    line.split("\t").forEach((part, index) => {
      if (index > 0) paragraph.append($createTabNode());
      if (part !== "") paragraph.append($createTextNode(part));
    });
    return paragraph;
  });
}

const $importText: ImportMimeTypeFunction = (text, selection, $next) => {
  const { limits } = $fragmentOptions();
  if (exceedsBytes(text, limits.maxBytes)) return $refuse("bytes", limits.maxBytes, "text");
  let lines = 1;
  for (let index = text.indexOf("\n"); index !== -1; index = text.indexOf("\n", index + 1)) lines++;
  // A line is a paragraph and its text.
  if (lines * 2 > limits.maxNodes) return $refuse("nodes", limits.maxNodes, "text");
  if ($isNodeSelection(selection)) {
    // Upstream's node selection ignores plain text, so a paste over selected blocks did nothing.
    selection.insertNodes($paragraphsFromText(text));
    return $accepted();
  }
  // Plain text is never guessed to be Markdown; "Paste as Markdown" is its own action.
  return $next() ? $accepted() : false;
};

/** Whether Markdown, or anything structured, may be inserted at this selection. */
export function $canInsertBlocks(selection: BaseSelection | null): boolean {
  if ($isNodeSelection(selection)) return true;
  if (!$isRangeSelection(selection)) return false;
  return $findMatchingParent(selection.anchor.getNode(), $isCodeNode) === null;
}

const $exportInternal = (selection: BaseSelection | null): string | null => {
  if (selection === null) return null;
  const { nodes } = $generateJSONFromSelectedNodes<SerializedNodeLike>($getEditor(), selection);
  if (nodes.length === 0) return null;
  return writeClipboardPayload($mounted().config.documentId, nodes);
};

/**
 * Copy, cut, paste, and text drag-and-drop, through Lexical's two clipboard extension points.
 *
 * Written: this package's versioned format, then Lexical's HTML and plain text as the fallbacks
 * every other application reads. Read, in that order, with one rule for all three: content is
 * checked like a stored document before any of it is inserted, and a paste over a limit is refused
 * whole. Lexical's own `application/x-lexical-editor` type is neither written nor read: it has no
 * version and upstream parses it unchecked.
 */
export const ClipboardExtension: LexicalExtension<
  ClipboardConfig,
  "z-editor/clipboard",
  ClipboardOutput,
  unknown
> = defineExtension({
  name: "z-editor/clipboard",
  config: safeCast<ClipboardConfig>(FALLBACK.config),
  dependencies: [
    configExtension(GetClipboardDataExtension, {
      $exportMimeType: {
        [CLIPBOARD_MIME_TYPE]: [$exportInternal],
        "application/x-lexical-editor": [() => null],
      },
    }),
    configExtension(ClipboardImportExtension, {
      $importMimeType: {
        [CLIPBOARD_MIME_TYPE]: [$importInternal],
        // Stacked above upstream's importers and never calling down to them.
        "application/x-lexical-editor": [() => false],
        "text/html": [$importHtml],
        "text/plain": [$importText],
        "text/uri-list": [$importText],
      },
      priority: { [CLIPBOARD_MIME_TYPE]: -10 },
    }),
  ],
  build(editor, config): ClipboardOutput {
    let current: Report = NOTHING;
    const listeners = new Set<() => void>();
    const report = (next: Report): void => {
      if (current.rejection === next.rejection && current.adjustment === next.adjustment) return;
      current = next;
      for (const listener of listeners) listener();
    };
    mounted.set(editor, { config, report });
    return {
      subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      getRejection: () => current.rejection,
      getAdjustment: () => current.adjustment,
      dismiss: () => report(NOTHING),
    };
  },
  register(editor) {
    // Lexical listens for clipboard events on its root element. While blocks are selected there is
    // no DOM range inside it, and Firefox then sends `paste` to <body> although the root has
    // focus, so a paste over selected blocks did nothing there. The events are handed over from
    // the document, only while blocks are selected and only when the root has focus.
    const route = (event: ClipboardEvent): void => {
      const root = editor.getRootElement();
      if (root === null || root.ownerDocument.activeElement !== root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      if (event.type === "copy") editor.dispatchCommand(COPY_COMMAND, event);
      else if (!editor.isEditable()) return;
      else editor.dispatchCommand(event.type === "cut" ? CUT_COMMAND : PASTE_COMMAND, event);
    };
    const TYPES = ["copy", "cut", "paste"] as const;
    let listening: Document | null = null;
    const listen = (target: Document | null): void => {
      if (target === listening) return;
      for (const type of TYPES) listening?.removeEventListener(type, route);
      for (const type of TYPES) target?.addEventListener(type, route);
      listening = target;
    };
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        const blocksSelected = editorState.read(() => $isNodeSelection($getSelection()));
        listen(blocksSelected ? (editor.getRootElement()?.ownerDocument ?? null) : null);
      }),
      () => listen(null),
    );
  },
});
